// Ported from dev-scrips-pm-react src/billing/components/invoice-receipt-actions.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { Ban, Printer } from 'lucide-react';
import { ReceiptActionButton } from '@/billing/components/payment-receipt-card';
import type { PaymentRow } from '@/shared/types/billing';

/**
 * Billing-surface action set for a payment receipt card — the slot passed to
 * `<PaymentReceiptCard actions={…}>` on the appointment / patient Billing tab
 * (`InvoiceDetailPage`). Distinct from the claim-popup slot (Print + View
 * Receipt); this surface is **Print + Void** (Refund was removed per PROD-1006
 * review — Flutter `invoice_payment_widget.dart` shows Void on this surface).
 *
 * Extracted from InvoiceDetailPage so the slot contract — *which* buttons
 * render for *which* payment status — is unit-testable in isolation rather than
 * riding on a full-page render mock (Samer review PR #448 §2).
 *
 * Gate: Print + Void show only on an **actionable** row (status not Voided /
 * Refunded). A voided row shows no actions — the backend PDF endpoint may not
 * generate a valid receipt for a voided payment (PR #112), and a voided payment
 * cannot be voided again.
 */
const NON_ACTIONABLE_STATUSES = new Set(['Voided', 'Refunded']);

interface Props {
  payment: PaymentRow;
  invoiceId: string;
  /** True while a print request is in flight — disables + relabels Print. */
  printPending?: boolean;
  onPrint: (args: { receiptId: string; invoiceId: string }) => void;
  onVoid: (payment: PaymentRow) => void;
}

export function InvoiceReceiptActions({
  payment,
  invoiceId,
  printPending = false,
  onPrint,
  onVoid,
}: Props) {
  const actionable = !NON_ACTIONABLE_STATUSES.has(payment.status ?? '');
  if (!actionable) return null;

  return (
    <>
      {payment.id && (
        <ReceiptActionButton
          icon={<Printer />}
          label={printPending ? 'Opening…' : 'Print'}
          disabled={printPending}
          onClick={() => onPrint({ receiptId: payment.id!, invoiceId })}
        />
      )}
      <ReceiptActionButton
        icon={<Ban />}
        label="Void"
        tone="error"
        onClick={() => onVoid(payment)}
      />
    </>
  );
}
